import { DefaultProps } from "@deck.gl/core"
import { LineLayer } from "@deck.gl/layers"
import { VideoMetadata } from "@nova/slice-video"
import { TerrainCache } from "./terraincache"
import { getFOVFromFocalLength } from "plugins/Video/FMV/useVideoProcessing"

type ElementType<T> = T extends (infer U)[] ? U : never
const terrainCache = new TerrainCache()

const yValuesToTry = Array.from({ length: 8 }).map((_, i) => -0.35 + i * 0.05)
export const getVideoFrameBbox = (frame: ElementType<VideoMetadata>, callback?: () => void) => {
    const { tan, cos, sin, PI, sqrt, pow } = Math
    const [longitude, latitude, relativeAltitude, pitch, yaw, roll, fov, altitude] = frame
    const degToRad = PI / 180.0
    let cutOff = false
    const [fovX, fovY] = Array.isArray(fov)
        ? fov.map((e) => e * degToRad)
        : getFOVFromFocalLength(fov)
    // Relative meters to Geographical
    const latRadians = degToRad * latitude
    const a = 6378137.0
    const f = 1.0 / 298.257223563
    const e = sqrt(2.0 * f - f ** 2)
    const metersPerLon =
        degToRad * ((a * cos(latRadians)) / pow(1.0 - e ** 2 * sin(latRadians) ** 2, 0.5))
    const metersPerLat =
        degToRad * ((a * (1.0 - e ** 2)) / pow(1.0 - e ** 2 * sin(latRadians) ** 2, 1.5))
    const relativeToGeo = ([x, y]: number[]) => [
        longitude + x / metersPerLon,
        latitude + y / metersPerLat,
    ]
    // From local relative coordinates [meters East, meters North] to geo [degrees lon, degrees lat]
    // Could use rel_alt instead but less accurate and not always available
    const elevation = terrainCache.elevation(longitude, latitude, callback, true)
    const height = Math.max(
        elevation === undefined || altitude === undefined ? relativeAltitude : altitude - elevation,
        0
    )
    if (!height) return undefined

    const getRay = (coord: number[]) => {
        const [ray] = [coord]
            .map(([x, y]) => [x * 2 * tan(fovX / 2), y * 2 * tan(fovY / 2)])
            .map(([x, y]) => ({
                // Nadir-referenced extrinsic rotation: pitch about world-fixed lateral axis first,
                // then roll about world-fixed forward axis (Ry(roll) × Rx(pitch+90°)).
                // Roll at nadir produces obliquity, not in-plane spin (body-fixed Tait-Bryan).
                dx:
                    cosYaw * sinRoll * sinPitch +
                    sinYaw * cosPitch +
                    cosYaw * cosRoll * x +
                    (sinYaw * sinPitch - cosYaw * sinRoll * cosPitch) * y,
                dy:
                    cosYaw * cosPitch -
                    sinYaw * sinRoll * sinPitch -
                    sinYaw * cosRoll * x +
                    (cosYaw * sinPitch + sinYaw * sinRoll * cosPitch) * y,
                // Fail gently if the FOV is above the horizon
                dz: -cosRoll * sinPitch + sinRoll * x + cosRoll * cosPitch * y,
            }))
        if (ray.dz < 0) return undefined
        // Iterative projections using previous location to determine height
        let point = [(ray.dx * height) / ray.dz, (ray.dy * height) / ray.dz] as number[] | undefined
        for (let step = 0; step < 3; step++) {
            ;[point] = [ray].map((ray) => {
                if (point) {
                    if (isTooLong(point)) return undefined
                    const prevPoint = relativeToGeo(point) // previous location
                    const elevation = terrainCache.elevation(prevPoint[0], prevPoint[1])
                    const iterativeHeight = Math.max(
                        elevation === undefined || altitude === undefined
                            ? relativeAltitude
                            : altitude - elevation,
                        0
                    )
                    if (!iterativeHeight) return undefined

                    return [
                        (ray.dx * iterativeHeight) / ray.dz,
                        (ray.dy * iterativeHeight) / ray.dz,
                    ]
                }
            })
        }
        return point
    }

    const isTooLong = ([x, y]: number[]) => {
        const maxVisibility = 2e4 // 20km

        const dist = sqrt(x ** 2 + y ** 2)
        if (dist > maxVisibility) {
            return true
        } else {
            return false
        }
    }
    const yawRad = degToRad * yaw
    const cosYaw = cos(yawRad)
    const sinYaw = sin(yawRad)

    const pitchRad = degToRad * pitch
    const cosPitch = cos(pitchRad)
    const sinPitch = sin(pitchRad)

    const rollRad = degToRad * roll
    const cosRoll = cos(rollRad)
    const sinRoll = sin(rollRad)

    const corners = [
        [-0.5, -0.5],
        [0.5, -0.5],
        [0.5, 0.5],
        [-0.5, 0.5],
        [0, 0],
    ]
    const rays = corners.map((ray) => getRay(ray))
    const valid = yValuesToTry.some((y) => {
        if (rays.slice(0, 2).some((e) => !e)) {
            rays[0] = getRay([-0.5, y])
            rays[1] = getRay([0.5, y])

            cutOff = true
            return false
        } else {
            return true
        }
    })
    if (!valid) return undefined

    const coords = (rays as number[][]).map(relativeToGeo)
    const box = coords.slice(0, -1)
    box.push(box.shift()!)
    const lines = []
    for (let i = 0; i < box.length; i++) {
        if (i === box.length - 1) {
            if (cutOff) break
            lines.push({
                start: box[i],
                end: box[0],
            })
        } else {
            lines.push({
                start: box[i],
                end: box[i + 1],
            })
        }
    }
    return [lines, coords.at(-1)!]
}

export type VideoFrameBoundingBox = ReturnType<typeof getVideoFrameBbox>

export const getVideoFrameBboxAsync = async (frame: ElementType<VideoMetadata>) => {
    const { tan, cos, sin, PI, sqrt, pow } = Math
    const [longitude, latitude, relativeAltitude, pitch, yaw, roll, fov, altitude, hasLRF] = frame
    const degToRad = PI / 180.0
    const [fovX, fovY] = Array.isArray(fov)
        ? fov.map((e) => e * degToRad)
        : getFOVFromFocalLength(fov)
    // Using Mapbox terrain DEM
    // Could use rel_alt instead but less accurate and not always available

    let height
    let heightDiff = 0

    const elevation = await new Promise<number>((resolve) =>
        terrainCache.elevation(longitude, latitude, resolve)
    )
    if (hasLRF) {
        height = relativeAltitude
        heightDiff = relativeAltitude - (altitude - elevation)
    } else {
        height = elevation === undefined ? relativeAltitude : altitude - elevation
    }

    const yawRad = degToRad * yaw
    const cosYaw = cos(yawRad)
    const sinYaw = sin(yawRad)

    const pitchRad = degToRad * pitch
    const cosPitch = cos(pitchRad)
    const sinPitch = sin(pitchRad)

    const rollRad = degToRad * roll
    const cosRoll = cos(rollRad)
    const sinRoll = sin(rollRad)

    const corners = [
        [-0.5, -0.5],
        [0.5, -0.5],
        [0.5, 0.5],
        [-0.5, 0.5],
    ]
    const rays = corners
        .map(([x, y]) => [x * 2 * tan(fovX / 2), y * 2 * tan(fovY / 2)])
        .map(([x, y]) => {
            // Nadir-referenced extrinsic rotation: pitch about world-fixed lateral axis first,
            // then roll about world-fixed forward axis (Ry(roll) × Rx(pitch+90°)).
            // Roll at nadir produces obliquity, not in-plane spin (body-fixed Tait-Bryan).
            const dz = -cosRoll * sinPitch + sinRoll * x + cosRoll * cosPitch * y
            // dz <= 0 means this corner is above the nadir plane (above the horizon).
            // Matches stitch's project_to_nadir: return None when v2.z >= 0.
            if (dz <= 0) return null
            return {
                dx:
                    cosYaw * sinRoll * sinPitch +
                    sinYaw * cosPitch +
                    cosYaw * cosRoll * x +
                    (sinYaw * sinPitch - cosYaw * sinRoll * cosPitch) * y,
                dy:
                    cosYaw * cosPitch -
                    sinYaw * sinRoll * sinPitch -
                    sinYaw * cosRoll * x +
                    (cosYaw * sinPitch + sinYaw * sinRoll * cosPitch) * y,
                dz,
            }
        })

    // If any corner is above the nadir plane, skip the image entirely.
    // Matches stitch's project_ortho_bounds: an image where any perimeter corner
    // returns None is dropped from bounds/overlap computation.
    if (rays.some((r) => r === null)) return undefined
    const validRays = rays as NonNullable<(typeof rays)[number]>[]

    // Relative meters to Geographical
    const latRadians = degToRad * latitude
    const a = 6378137.0
    const f = 1.0 / 298.257223563
    const e = sqrt(2.0 * f - f ** 2)
    const metersPerLon =
        degToRad * ((a * cos(latRadians)) / pow(1.0 - e ** 2 * sin(latRadians) ** 2, 0.5))
    const metersPerLat =
        degToRad * ((a * (1.0 - e ** 2)) / pow(1.0 - e ** 2 * sin(latRadians) ** 2, 1.5))

    // From local relative coordinates [meters East, meters North] to geo [degrees lon, degrees lat]
    const relativeToGeo = ([x, y]: number[]) =>
        [longitude + x / metersPerLon, latitude + y / metersPerLat] satisfies [number, number]

    // Limit the furthest distance projected
    const maxVisibility = 2e4 // 20km
    const limitVisibility = ([x, y]: number[]) => {
        const dist = sqrt(x ** 2 + y ** 2)
        if (dist > maxVisibility) {
            const ratio = dist / maxVisibility
            return [x / ratio, y / ratio]
        } else {
            return [x, y]
        }
    }

    // Initial projection using height of camera
    let bbox = validRays.map((ray) =>
        limitVisibility([(ray.dx * height) / ray.dz, (ray.dy * height) / ray.dz])
    )

    // Iterative projections using previous location to determine height
    for (let step = 0; step < 3; step++) {
        bbox = await Promise.all(
            validRays.map(async (ray, i) => {
                const point = relativeToGeo(bbox[i]) // previous location
                const elevation = await new Promise<number>((resolve) =>
                    terrainCache.elevation(point[0], point[1], resolve)
                )
                const iterativeHeight =
                    elevation === undefined || altitude === undefined
                        ? relativeAltitude
                        : altitude - elevation + heightDiff
                return limitVisibility([
                    (ray.dx * iterativeHeight) / ray.dz,
                    (ray.dy * iterativeHeight) / ray.dz,
                ])
            })
        )
    }

    return bbox.map(relativeToGeo)
}

const defaultProps: DefaultProps = {
    getSourcePosition: (d: { start: number[]; end: number[] } | undefined) => {
        if (d) return d.start
        else return []
    },
    getTargetPosition: (d: { start: number[]; end: number[] } | undefined) => {
        if (d) return d.end
        else return []
    },
    getColor: [64, 170, 255, 255],
    getWidth: 3,
    widthMinPixels: 1,
    pickable: false,
}

export default class VideoProjectionLayer extends LineLayer {
    static defaultProps = defaultProps
    static layerName = "VideoProjectionLayer"
}
